import React, { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import usePageSeo, { categorySeo } from "../hooks/usePageSeo";
import { CartContext } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import ProductCard from "../components/ProductCard";
import "../css/shop.css";
import "../css/SubCategory.css";
import api from "../services/api";

const Category = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { addToCart } = useContext(CartContext);
  const { isAuthenticated } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoryInfo, setCategoryInfo] = useState(null);

  usePageSeo(categorySeo(categoryInfo, "category"));

  useEffect(() => {
    if (id) {
      setLoading(true);

      // Fetch category info
      const fetchCategoryInfo = async () => {
        try {
          const categoryRes = await api.get(`/categories/${id}`);
          setCategoryInfo(categoryRes.data);
        } catch (err) {
          console.error("Error fetching category info:", err);
        }
      };

      // Fetch products by category
      const fetchProducts = async () => {
        try {
          const res = await api.get(`/categories/${id}/products`);
          console.log("Category Products API Response:", res.data);
          const productsData = Array.isArray(res.data)
            ? res.data
            : res.data.products || [];
          setProducts(productsData);

          // The endpoint already ships each product's images — no extra requests.
          const imagesData = {};
          productsData.forEach((product) => {
            const images = Array.isArray(product.images) ? product.images : [];
            const mainImage = images.find((img) => img.is_main) || images[0];
            imagesData[product._id] = mainImage?.url || "/images/default-product.jpg";
          });
          setProductImages(imagesData);
        } catch (err) {
          console.error("Error fetching products:", err);
        } finally {
          setLoading(false);
        }
      };

      Promise.all([fetchCategoryInfo(), fetchProducts()]);
    }
  }, [id]);

  const handleAddToCart = async (product) => {
    if (!isAuthenticated) {
      toast.error("Please login for further process.", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    try {
      await api.post("/cart", {
        product_id: product._id,
        quantity: 1,
      });

      addToCart({
        ...product,
        newPrice: product.is_on_sale ? product.sale_price : product.sell_price,
        quantity: 1,
      });

      toast.success(t("product.addedToCart"), {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error adding product to cart:", error);
      toast.error(t("product.addToCartFailed"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  if (loading)
    return <div className="text-center mt-5">{t("shop.loading")}</div>;

  return (
    <div className="container-fluid">
      <ToastContainer />

      {/* Breadcrumb */}
      <div className="breadcrumb-container breadcrumb-gray-theme">
        <nav aria-label="breadcrumb" className="breadcrumb-nav">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/" className="breadcrumb-link">
                {t("nav.home")}
              </Link>
              <span className="breadcrumb-separator"></span>
            </li>

            <li className="breadcrumb-item active" aria-current="page">
              <span className="breadcrumb-subcategory">
                {categoryInfo?.name}
              </span>
            </li>
          </ol>
        </nav>
      </div>

      <div className="container py-4">
        {products.length === 0 ? (
          <div className="text-center mt-5">{t("shop.noProducts")}</div>
        ) : (
          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-4">
            {products.map((product) => {
              const price = product.is_on_sale
                ? product.sale_price
                : product.sell_price;
              const imageUrl =
                productImages[product._id] || "/images/default-product.jpg";

              return (
                <div className="col" key={product._id}>
                  <ProductCard
                    product={{ ...product, newPrice: price }}
                    productImageUrl={imageUrl}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Category;
